// src/components/messaging/CreateGroupDialog.tsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  CircularProgress,
  Box,
  Checkbox,
  Chip,
  Stack,
  alpha,
} from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import { useMessaging } from '../../contexts/MessagingContext';
import { useTranslation } from 'react-i18next';
import { MessagingUser, Contact } from '../../types/messaging';

interface CreateGroupDialogProps {
  open: boolean;
  onClose: () => void;
}

const CreateGroupDialog: React.FC<CreateGroupDialogProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { state, fetchContacts, createGroup } = useMessaging();
  const { contacts } = state;

  const [groupName, setGroupName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<MessagingUser[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch contacts on open
  useEffect(() => {
    if (open) {
      setIsLoading(true);
      fetchContacts().finally(() => setIsLoading(false));
      setGroupName('');
      setDescription('');
      setSelectedMembers([]);
      setError(null);
    }
  }, [open, fetchContacts]);

  // Toggle member selection
  const toggleMember = (user: MessagingUser) => {
    setSelectedMembers((prev) => {
      const isSelected = prev.some((m) => m.id === user.id);
      if (isSelected) {
        return prev.filter((m) => m.id !== user.id);
      }
      return [...prev, user];
    });
  };

  // Remove member chip
  const removeMember = (userId: number) => {
    setSelectedMembers((prev) => prev.filter((m) => m.id !== userId));
  };

  // Handle create
  const handleCreate = async () => {
    if (!groupName.trim()) {
      setError(t('messaging.groupNameRequired', 'Le nom du groupe est requis'));
      return;
    }
    if (selectedMembers.length === 0) {
      setError(t('messaging.selectMembers', 'Sélectionnez au moins un membre'));
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      await createGroup(
        groupName.trim(),
        description.trim(),
        selectedMembers.map((m) => m.id)
      );
      onClose();
    } catch (err) {
      console.error('Failed to create group:', err);
      setError(t('messaging.groupCreateError', 'Erreur lors de la création du groupe'));
    } finally {
      setIsCreating(false);
    }
  };

  // Get initials
  const getInitials = (name?: string): string => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  // Extract users from contacts - use proper typing
  // API uses `user` field, fallback to `contact` for compatibility
  const contactUsers = (Array.isArray(contacts) ? contacts : [])
    .map((c: Contact) => c.user || c.contact)
    .filter((u): u is MessagingUser => u !== undefined && u !== null);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('messaging.createGroup', 'Créer un groupe')}</DialogTitle>
      <DialogContent>
        {/* Group name */}
        <TextField
          fullWidth
          label={t('messaging.groupName', 'Nom du groupe')}
          value={groupName}
          onChange={(e) => setGroupName(e.target.value)}
          sx={{ mb: 2, mt: 1 }}
          error={!!error && !groupName.trim()}
        />

        {/* Description */}
        <TextField
          fullWidth
          label={t('messaging.groupDescription', 'Description (optionnel)')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          rows={2}
          sx={{ mb: 2 }}
        />

        {/* Selected members */}
        {selectedMembers.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('messaging.selectedMembers', 'Membres sélectionnés')} ({selectedMembers.length})
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {selectedMembers.map((member) => (
                <Chip
                  key={member.id}
                  label={member.name}
                  onDelete={() => removeMember(member.id)}
                  size="small"
                  avatar={
                    <Avatar src={member.avatar_url || undefined} sx={{ width: 24, height: 24 }}>
                      {getInitials(member.name)}
                    </Avatar>
                  }
                />
              ))}
            </Stack>
          </Box>
        )}

        {/* Contact list */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t('messaging.addMembers', 'Ajouter des membres')}
        </Typography>

        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 4,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        ) : contactUsers.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 4,
              color: 'text.secondary',
            }}
          >
            <GroupIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
            <Typography variant="body2">
              {t('messaging.noContacts', 'Aucun contact')}
            </Typography>
            <Typography variant="caption" sx={{ mt: 1 }}>
              {t('messaging.addContactsFirst', 'Ajoutez des contacts pour créer un groupe')}
            </Typography>
          </Box>
        ) : (
          <List sx={{ maxHeight: 250, overflow: 'auto' }}>
            {contactUsers.map((user) => {
              const isSelected = selectedMembers.some((m) => m.id === user.id);
              return (
                <ListItemButton
                  key={user.id}
                  onClick={() => toggleMember(user)}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    backgroundColor: isSelected
                      ? (theme) => alpha(theme.palette.primary.main, 0.08)
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.12),
                    },
                  }}
                >
                  <Checkbox
                    checked={isSelected}
                    sx={{ mr: 1 }}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggleMember(user)}
                  />
                  <ListItemAvatar>
                    <Avatar src={user.avatar_url || undefined}>
                      {getInitials(user.name)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={user.name}
                    secondary={user.email}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}

        {/* Error */}
        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isCreating}>
          {t('common.cancel', 'Annuler')}
        </Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={isCreating || !groupName.trim() || selectedMembers.length === 0}
        >
          {isCreating ? <CircularProgress size={20} /> : t('common.create', 'Créer')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateGroupDialog;
